import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useUrlParams from '../hooks/useUrlParams';
import { MemberIndexEntry, Party } from '../types/types';
import Layout from '../components/Layout';
import RoleTag from '../components/RoleTag';
import { hasCoalition, shortPartyName } from '../utils/party';
import { photoFor } from '../utils/names';
import { useBreakpoint } from '../useBreakpoint';
import Grid from '../layout/Grid';
import PageContainer from '../layout/PageContainer';
import TwoPane from '../layout/TwoPane';
import { getAllMembersOf, getParties, getPartyChairLobbyistsOf, getPartyMemberExpenses, MemberExpenses, PartyChairLobbyists, UNTAGGED_KNESSET } from '../data/cache';
import { MOBILE_NAV_CLEARANCE } from '../components/MobileShell';
import { buttonReset, color, font, radius, shadow, space, text, weight } from '../theme';
import DetailHeader from '../components/DetailHeader';
import TabBar from '../components/TabBar';
import { Card, CardHeader } from '../components/Card';
import { StatTile, StatTileRow } from '../components/StatTile';
import MetricsCard, { MetricGroup } from '../components/MetricsCard';
import { ResultBar } from '../components/SelectMenu';
import { Badge } from '../components/Badge';
import Bar from '../components/Bar';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import Icon from '../components/Icon';
import PresenceLegend from '../components/PresenceLegend';
import { usePresenceNow } from '../hooks/usePresence';
import { isPresenceStale } from '../utils/presence';
import { EmptyState, ErrorState } from '../components/States';
import { Skeleton, SkeletonList, SkeletonText, SkeletonTiles } from '../components/Skeleton';
import PartyFundingTab from '../components/funding/PartyFunding';

/* ── Types ──────────────────────────────────────────────────────────────── */

interface PartyMember extends MemberIndexEntry {
  age?: number;
}

type PartyTab = 'members' | 'manifesto' | 'funding';

const toPartyMember = (m: MemberIndexEntry): PartyMember => ({
  ...m,
  age: m.birthYear ? new Date().getFullYear() - m.birthYear : undefined,
});

const isWoman = (m: PartyMember) => m.gender === 'נקבה';
const fmtShekels = (n: number) => '₪' + Math.round(n).toLocaleString('he-IL');

/* ── Seat bar ────────────────────────────────────────────────────────────── */

// Every party's seats in one bar, coalition first; each segment links to its party
const SeatBar = ({ allParties, currentPartyId }: { allParties: Party[]; currentPartyId: string }) => {
  if (allParties.length === 0) return null;
  // Before a coalition exists the bar is just the seats, largest first
  const blocs = hasCoalition(allParties);
  const sorted = allParties
    .filter(p => p.seats > 0)
    .sort((a, b) => (blocs && a.isCoalition !== b.isCoalition ? (a.isCoalition ? -1 : 1) : b.seats - a.seats));
  const coalitionTotal = sorted.filter(p => p.isCoalition).reduce((s, p) => s + p.seats, 0);
  const oppositionTotal = sorted.filter(p => !p.isCoalition).reduce((s, p) => s + p.seats, 0);
  const total = coalitionTotal + oppositionTotal;
  const seatsTotal = sorted.reduce((s, p) => s + p.seats, 0);

  return (
    <div>
      {blocs ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: font.small, color: color.textSecondary, marginBottom: space.sm }}>
          <span>קואליציה <b style={{ color: color.coalition }}>{coalitionTotal}</b></span>
          <span>אופוזיציה <b style={{ color: color.oppositionText }}>{oppositionTotal}</b></span>
        </div>
      ) : (
        <div style={{ fontSize: font.small, color: color.textSecondary, marginBottom: space.sm, textAlign: 'center' }}>טרם הוקמה קואליציה</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, height: 18 }}>
        {sorted.map(p => {
          const isCurrent = p.id === currentPartyId;
          return (
            <Link
              key={p.id}
              to={`/parties/${p.id}`}
              title={`${shortPartyName(p.name)}: ${p.seats} מנדטים`}
              aria-label={`${shortPartyName(p.name)}, ${p.seats} מנדטים`}
              aria-current={isCurrent ? 'page' : undefined}
              style={{
                width: `${(p.seats / (seatsTotal || total || 1)) * 100}%`,
                height: isCurrent ? 18 : 12,
                background: p.color || '#98a2b3',
                opacity: isCurrent ? 1 : 0.35,
                borderRadius: isCurrent ? 4 : 2,
                transition: 'opacity 0.15s',
              }}
            />
          );
        })}
      </div>
      <div style={{ fontSize: font.caption, color: color.textSecondary, textAlign: 'center', marginTop: space.xs + 2 }}>120 מושבים בכנסת</div>
    </div>
  );
};

/* ── Members tab ─────────────────────────────────────────────────────────── */

/* Option A (owner, 2026-10-10). The party's figures sit on tiles in four groups (up to
   three, four in חקיקה since 5c-B, 2026-10-17); a tile sorts the list by its metric (tap it again to flip the direction, the
   arrow shows which way). Filters are a separate, compact row of chips that all stay in
   view and combine with any sort. A status line says what the list shows, with a reset.
   The sort, direction and filter live in the URL, so Back returns to the same view. */

type SortKey = 'age' | 'seniority' | 'committees' | 'bills' | 'inProgress' | 'passed' | 'passRate' | 'attendance' | 'queries' | 'agendaMotions' | 'expenses' | 'lobbyists';
type FilterKey = 'all' | 'women' | 'men' | 'ministers' | 'norwegian' | 'newMks' | 'veterans';

const SORT_KEYS: SortKey[] = ['age', 'seniority', 'committees', 'bills', 'inProgress', 'passed', 'passRate', 'attendance', 'queries', 'agendaMotions', 'expenses', 'lobbyists'];

const METRIC_GROUPS: MetricGroup<SortKey>[] = [
  { key: 'profile', label: 'פרופיל', keys: ['age', 'seniority', 'committees'] },
  // 5c-B: the pass rate sits with the bills, and activity gains the motions for the agenda
  { key: 'legislation', label: 'חקיקה', keys: ['bills', 'inProgress', 'passed', 'passRate'] },
  { key: 'activity', label: 'פעילות', keys: ['attendance', 'queries', 'agendaMotions'] },
  { key: 'transparency', label: 'שקיפות', keys: ['expenses', 'lobbyists'] },
];

// The tile label, and the phrase in the status line ("לפי נוכחות")
const METRIC_LABEL: Record<SortKey, string> = {
  age: 'גיל ממוצע', seniority: 'ותק ממוצע', committees: 'ועדות',
  bills: 'הגשות', inProgress: 'בתהליך', passed: 'חוקים שעברו',
  passRate: 'אחוז הצלחה', attendance: 'נוכחות', queries: 'שאילתות', agendaMotions: 'הצעות לסדר',
  expenses: 'הוצאות ממוצע', lobbyists: 'שדלנים בוועדות',
};
const METRIC_PHRASE: Record<SortKey, string> = {
  age: 'גיל', seniority: 'ותק', committees: 'מספר ועדות',
  bills: 'הגשות', inProgress: 'הצעות בתהליך', passed: 'חוקים שעברו',
  passRate: 'אחוז הצלחה', attendance: 'נוכחות', queries: 'שאילתות', agendaMotions: 'הצעות לסדר היום',
  expenses: 'הוצאות', lobbyists: 'שדלנים בוועדות',
};

const FILTERS: { key: FilterKey; label: string; noun: string }[] = [
  { key: 'all', label: 'הכל', noun: 'חברי כנסת' },
  { key: 'women', label: 'נשים', noun: 'חברות כנסת' },
  { key: 'men', label: 'גברים', noun: 'חברי כנסת' },
  { key: 'ministers', label: 'שרים', noun: 'שרים' },
  { key: 'norwegian', label: 'נורווגיים', noun: 'שרים נורווגיים' },
  { key: 'newMks', label: 'חדשים', noun: 'ח״כים חדשים' },
  { key: 'veterans', label: 'ותיקים', noun: 'ותיקים (3 כנסות ומעלה)' },
];


// `knesset`: the Knesset the party sat in; an earlier Knesset's MKs come from its archived index
const MembersList = ({ partyId, partyName, chairLobbyists, knesset }: { partyId: string; partyName: string; chairLobbyists: PartyChairLobbyists | null; knesset?: number }) => {
  const { isDesktop } = useBreakpoint();
  const [params, setParams] = useUrlParams();
  const [members, setMembers] = useState<PartyMember[] | null>(null);
  const [norwegianMinisters, setNorwegianMinisters] = useState<PartyMember[]>([]);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [expenseDocs, setExpenseDocs] = useState<MemberExpenses[] | null>(null);
  const [expenseError, setExpenseError] = useState(false);
  const [expenseSubView, setExpenseSubView] = useState<'vendors' | 'categories' | null>(null);
  const [expenseAllVendors, setExpenseAllVendors] = useState(false);
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null);
  const statTilesRef = useRef<HTMLDivElement>(null);
  // Who is in the Knesset building now: dots on the photos, while the data is fresh
  const presence = usePresenceNow();
  const presentNow: Record<string, string> = presence.now && !isPresenceStale(presence.now, presence.clock) ? presence.now.present : {};

  const sortParam = params.get('sort') as SortKey | null;
  const sort: SortKey | null = sortParam && SORT_KEYS.indexOf(sortParam) >= 0 ? sortParam : null;
  const dir: 'asc' | 'desc' = params.get('dir') === 'asc' ? 'asc' : 'desc';
  const filterParam = params.get('filter') as FilterKey | null;
  const filter: FilterKey = filterParam && FILTERS.some(f => f.key === filterParam) ? filterParam : 'all';

  const update = useCallback((updates: Record<string, string | null>) => setParams(prev => {
    const next = new URLSearchParams(prev);
    Object.keys(updates).forEach(k => { const v = updates[k]; if (v === null) next.delete(k); else next.set(k, v); });
    return next;
  }), [setParams]);

  // Sitting MKs and the party's Norwegian Law ministers, from the member index
  useEffect(() => {
    let cancelled = false;
    setError(false);
    getAllMembersOf(knesset).then(all => {
      if (cancelled) return;
      const mine = all.filter(m => m.partyId === partyId);
      setMembers(mine.filter(m => m.isCurrentMk === true).map(toPartyMember));
      setNorwegianMinisters(mine.filter(m => m.governmentMember && m.isActiveMk === false).map(toPartyMember));
    }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [partyId, knesset, reloadKey]);

  // The expense breakdown needs full member docs, so they load only when sorting by expenses
  useEffect(() => {
    if (sort !== 'expenses' || expenseDocs) return;
    let cancelled = false;
    setExpenseError(false);
    getPartyMemberExpenses(partyId)
      .then(docs => { if (!cancelled) setExpenseDocs(docs); })
      .catch(() => { if (!cancelled) setExpenseError(true); });
    return () => { cancelled = true; };
  }, [sort, expenseDocs, partyId]);

  // A tile sorts high to low; the sorted tile flips the direction
  const pickSort = (k: SortKey) => {
    if (sort === k) update({ dir: dir === 'desc' ? 'asc' : null });
    else update({ sort: k, dir: null });
    if (k !== 'expenses') { setExpenseSubView(null); setExpenseAllVendors(false); }
  };
  const pickFilter = (f: FilterKey) => update({ filter: f === 'all' ? null : f });
  const reset = () => update({ sort: null, dir: null, filter: null });

  // The floating "back to the tiles" button shows once the tiles scroll out of view
  const membersReady = members !== null;
  useEffect(() => {
    const el = statTilesRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setShowScrollUp(!entry.isIntersecting), { threshold: 0 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [membersReady]);

  const getStatValue = useCallback((m: PartyMember): number | null => {
    switch (sort) {
      case 'age': return m.age || null;
      case 'attendance': return m.attendancePct != null ? Math.round(m.attendancePct) : null;
      case 'committees': return m.committeeIds?.length || null;
      case 'bills': return m.billStats?.led || null;
      case 'passed': return m.billStats?.passed || null;
      case 'inProgress': return m.billStats?.inProgress || null;
      case 'passRate': return (m.billStats && m.billStats.led >= 3) ? Math.round((m.billStats.passed / m.billStats.led) * 100) : null;
      case 'queries': return m.queryCount || null;
      case 'agendaMotions': return m.agendaMotionCount || null;
      case 'seniority': return m.yearsInPolitics || null;
      case 'lobbyists': return m.lobbyistCount || null;
      case 'expenses': return m.expenseTotal || null;
      default: return null;
    }
  }, [sort]);

  const formatStatValue = (v: number): string => {
    if (sort === 'attendance' || sort === 'passRate') return `${v}%`;
    if (sort === 'age') return `בן/ת ${v}`;
    if (sort === 'committees') return `${v} ועדות`;
    if (sort === 'seniority') return `${v} שנים`;
    if (sort === 'expenses') return `₪${Math.round(v / 1000)}K`;
    return String(v);
  };

  const sitting = members || [];
  const filterTest: Record<FilterKey, (m: PartyMember) => boolean> = {
    all: () => true,
    women: isWoman,
    men: m => !isWoman(m),
    ministers: m => !!m.governmentMember && m.isActiveMk !== false,
    norwegian: () => true,
    newMks: m => m.knessetCount === 1,
    veterans: m => (m.knessetCount || 0) >= 3,
  };
  const filterSource = (f: FilterKey) => (f === 'norwegian' ? norwegianMinisters : sitting);
  const filterCount = (f: FilterKey) => filterSource(f).filter(filterTest[f]).length;

  if (error) return <div style={{ padding: isDesktop ? 0 : space.lg }}><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></div>;
  if (!members) {
    return (
      <div data-skeleton="true" style={{ padding: isDesktop ? 0 : space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
        <Card><SkeletonTiles count={3} /></Card><SkeletonList rows={6} avatar />
      </div>
    );
  }

  /* ── The list: filter, then sort (a few dozen rows, so no memoizing) ── */
  const byName = (a: PartyMember, b: PartyMember) => a.name.localeCompare(b.name, 'he');
  const sorted = (filter === 'norwegian' ? norwegianMinisters : members.filter(filterTest[filter])).slice().sort((a, b) => {
    if (!sort) return byName(a, b);
    const av = getStatValue(a); const bv = getStatValue(b);
    if (av === null && bv === null) return byName(a, b);
    if (av === null) return 1;
    if (bv === null) return -1;
    return (dir === 'desc' ? bv - av : av - bv) || byName(a, b);
  });
  let maxStatVal: number | null = null;
  if (sort) for (const m of sorted) { const v = getStatValue(m); if (v !== null && (maxStatVal === null || v > maxStatVal)) maxStatVal = v; }

  /* ── The party's figures, one per sortable metric ── */
  const avg = (vals: number[]) => (vals.length > 0 ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : null);
  const ledBills = members.reduce((s, m) => s + (m.billStats?.led || 0), 0);
  const passedBills = members.reduce((s, m) => s + (m.billStats?.passed || 0), 0);
  const committeeSet = new Set<string>();
  members.forEach(m => (m.committeeIds || []).forEach(id => committeeSet.add(id)));
  const avgExpense = avg(members.map(m => m.expenseTotal || 0).filter(e => e > 0));
  const partyValue: Record<SortKey, React.ReactNode | null> = {
    age: avg(members.map(m => m.age).filter((a): a is number => typeof a === 'number')),
    seniority: avg(members.map(m => m.yearsInPolitics).filter((y): y is number => typeof y === 'number' && y > 0)),
    committees: committeeSet.size || null,
    bills: ledBills ? ledBills.toLocaleString('he-IL') : null,
    inProgress: members.reduce((s, m) => s + (m.billStats?.inProgress || 0), 0).toLocaleString('he-IL'),
    passed: passedBills ? passedBills.toLocaleString('he-IL') : null,
    passRate: ledBills > 0 ? `${Math.round((passedBills / ledBills) * 100)}%` : null,
    attendance: (() => { const a = avg(members.map(m => m.attendancePct).filter((x): x is number => typeof x === 'number')); return a !== null ? `${a}%` : null; })(),
    queries: members.reduce((s, m) => s + (m.queryCount || 0), 0).toLocaleString('he-IL'),
    agendaMotions: members.reduce((s, m) => s + (m.agendaMotionCount || 0), 0).toLocaleString('he-IL'),
    expenses: avgExpense ? `₪${Math.round(avgExpense / 1000)}K` : null,
    lobbyists: chairLobbyists ? chairLobbyists.lobbyists : null,
  };

  const statNote: Partial<Record<SortKey, string>> = {
    attendance: 'נוכחות בהצבעות בלבד. לנוכחות פיזית: zmanknesset.co.il',
    passRate: 'רק ח״כים עם 3 הצעות חוק או יותר',
    seniority: 'שנים מאז הכניסה הראשונה לכנסת',
    bills: 'הצעות שחבר/ת הכנסת בין שלושת היוזמים הראשונים שלהן',
    agendaMotions: 'הצעות לסדר היום שחבר/ת הכנסת הגיש/ה, לבד או כחלק מהצעה כוללת',
    lobbyists: chairLobbyists
      ? `${chairLobbyists.lobbyists} שדלנים שונים הופיעו ב-${chairLobbyists.committees} ועדות שבראשות חברי הסיעה (${chairLobbyists.appearances} הופעות). הרשימה ממוינת לפי השדלנים בוועדות של כל ח״כ.`
      : undefined,
  };

  // Filters with nothing to show are left out (e.g. ministers in an opposition party)
  const visibleFilters = FILTERS.filter(f => f.key === 'all' || f.key === filter || filterCount(f.key) > 0);
  const noun = (FILTERS.find(f => f.key === filter) || FILTERS[0]).noun;
  const presentHere = members.concat(norwegianMinisters).filter(m => !!presentNow[m.id]).length;
  const statusText = `${sorted.length} ${noun} · ${sort ? `לפי ${METRIC_PHRASE[sort]}, ${dir === 'desc' ? 'מהגבוה לנמוך' : 'מהנמוך לגבוה'}` : 'לפי שם'}`;

  /* ── Expense breakdown ── */
  const expenseBreakdown = () => {
    if (expenseError) return <Card padding={0}><ErrorState onRetry={() => { setExpenseError(false); setExpenseDocs(null); }} /></Card>;
    if (!expenseDocs) return <SkeletonList rows={3} />;
    // Sitting MKs plus the party's Norwegian Law ministers
    const docs = expenseDocs.filter(d => d.isCurrentMk === true || (d.governmentMember && d.isActiveMk === false));
    const ministerIds = new Set(docs.filter(d => d.governmentMember && d.isActiveMk === false).map(d => d.id));
    const names: Record<string, string> = {};
    for (const d of docs) names[d.id] = d.name;
    let totalPartySpending = 0;
    const cats: Record<string, number> = {};
    const vendors: Record<string, { total: number; mkIds: Set<string>; byMk: Record<string, number> }> = {};
    for (const d of docs) {
      const years = d.expenses ? Object.keys(d.expenses) : [];
      for (const y of years) {
        const yd = d.expenses![y];
        totalPartySpending += yd.total || 0;
        for (const cat of Object.keys(yd.byCategory || {})) cats[cat] = (cats[cat] || 0) + yd.byCategory[cat];
        for (const item of yd.items || []) {
          const v = (item.vendor || '').trim();
          if (!v) continue;
          if (!vendors[v]) vendors[v] = { total: 0, mkIds: new Set<string>(), byMk: {} };
          vendors[v].total += item.amount;
          vendors[v].mkIds.add(d.id);
          vendors[v].byMk[d.id] = (vendors[v].byMk[d.id] || 0) + item.amount;
        }
      }
    }
    const catEntries = Object.keys(cats).map(k => [k, cats[k]] as [string, number]).sort((a, b) => b[1] - a[1]);
    const maxCat = catEntries.length > 0 ? catEntries[0][1] : 1;
    const vendorEntries = Object.keys(vendors).map(k => [k, vendors[k]] as [string, typeof vendors[string]]).filter(([, v]) => v.total > 0).sort((a, b) => b[1].total - a[1].total);
    const maxVendor = vendorEntries.length > 0 ? vendorEntries[0][1].total : 1;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
        <StatTileRow columns={3}>
          <StatTile variant="flat" value={fmtShekels(totalPartySpending)} label="סה״כ הוצאות הסיעה" />
          <StatTile variant="flat" value={vendorEntries.length} label="ספקים" selected={expenseSubView === 'vendors'} indicator="filter"
            onClick={() => { setExpenseSubView(expenseSubView === 'vendors' ? null : 'vendors'); }} />
          <StatTile variant="flat" value={catEntries.length} label="קטגוריות" selected={expenseSubView === 'categories'} indicator="filter"
            onClick={() => { setExpenseSubView(expenseSubView === 'categories' ? null : 'categories'); setExpenseAllVendors(false); }} />
        </StatTileRow>

        {expenseSubView === 'vendors' && (
          <Card padding={0}>
            <div style={{ padding: `${space.lg}px ${space.lg}px ${space.xs}px` }}><CardHeader title="ספקים" meta="כל השנים" style={{ marginBottom: 0 }} /></div>
            {(expenseAllVendors ? vendorEntries : vendorEntries.slice(0, 10)).map(([vName, vData]) => {
              const isExpanded = expandedVendor === vName;
              const pct = totalPartySpending > 0 ? Math.round((vData.total / totalPartySpending) * 100) : 0;
              const ids = Array.from(vData.mkIds);
              const mins = ids.filter(id => ministerIds.has(id)).length;
              const mkList = isExpanded
                ? Object.keys(vData.byMk).map(id => ({ id, name: names[id] || id, amount: vData.byMk[id] })).sort((a, b) => b.amount - a.amount)
                : [];
              const maxMk = mkList.length > 0 ? Math.max(...mkList.map(mk => Math.abs(mk.amount)), 1) : 1;
              return (
                <div key={vName} style={{ borderTop: `1px solid ${color.divider}` }}>
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={() => setExpandedVendor(isExpanded ? null : vName)}
                    style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', padding: `${space.sm + 2}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.xs + 2, minHeight: 48 }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, width: '100%' }}>
                      <span style={{ color: color.textTertiary }}><Icon name={isExpanded ? 'chevronUp' : 'chevronDown'} size={16} /></span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: font.label, fontWeight: isExpanded ? weight.bold : weight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vName}</span>
                      {ids.length > 1 && <Badge tone="info">{mins > 0 ? `${ids.length - mins} ח״כים + ${mins} שרים` : `${ids.length} ח״כים`}</Badge>}
                      <span style={{ fontSize: font.small, color: color.textSecondary, flexShrink: 0 }}>{fmtShekels(vData.total)} ({pct}%)</span>
                    </span>
                    <Bar value={vData.total} max={maxVendor} height={6} style={{ width: '100%' }} />
                  </button>
                  {isExpanded && (
                    <div style={{ margin: `0 ${space.lg}px ${space.md}px`, background: color.surfaceMuted, borderRadius: radius.md, padding: `${space.xs}px ${space.md}px` }}>
                      {mkList.map((mk, i) => (
                        <Link key={mk.id} to={`/members/${mk.id}`} state={{ from: partyName, stat: 'expenses' }}
                          style={{ display: 'block', padding: `${space.sm}px 0`, borderTop: i > 0 ? `1px solid ${color.border}` : 'none', color: color.text, textDecoration: 'none' }}>
                          <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: font.small, marginBottom: space.xs }}>
                            <span style={{ color: color.primary, fontWeight: weight.medium }}>{mk.name}</span>
                            <span style={{ color: color.textSecondary }}>{fmtShekels(mk.amount)}</span>
                          </span>
                          <Bar value={Math.abs(mk.amount)} max={maxMk} height={4} track={color.surface} />
                        </Link>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: `${space.sm}px 0`, borderTop: `1px solid ${color.border}`, fontSize: font.small, fontWeight: weight.bold }}>
                        <span>סה״כ</span><span style={{ color: color.primary }}>{fmtShekels(vData.total)}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {vendorEntries.length > 10 && (
              <div style={{ padding: space.md, borderTop: `1px solid ${color.divider}` }}>
                <Button fullWidth onClick={() => setExpenseAllVendors(!expenseAllVendors)}>
                  {expenseAllVendors ? 'הצגת 10 המובילים בלבד' : `הצגת כל ${vendorEntries.length} הספקים`}
                </Button>
              </div>
            )}
          </Card>
        )}

        {expenseSubView === 'categories' && (
          <Card>
            <CardHeader title="לפי קטגוריה" meta="כל השנים" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
              {catEntries.map(([cat, amount]) => (
                <div key={cat}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: font.small, marginBottom: space.xs }}>
                    <span style={{ fontWeight: weight.medium }}>{cat}</span>
                    <span style={{ color: color.textSecondary }}>{fmtShekels(amount)} ({totalPartySpending > 0 ? Math.round((amount / totalPartySpending) * 100) : 0}%)</span>
                  </div>
                  <Bar value={amount} max={maxCat} height={6} />
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  /* ── MK rows ── */
  const mkRow = (member: PartyMember, i: number, inGrid: boolean) => {
    const primaryRole = member.roles?.find(r => r.isPrimary && r.title !== 'חבר/ת כנסת' && r.title !== 'חבר כנסת');
    const statVal = getStatValue(member);
    return (
      <ListRow
        key={member.id}
        divider={!inGrid && i > 0}
        to={`/members/${member.id}`}
        state={{ from: partyName, stat: sort || undefined }}
        leading={<Avatar name={member.name} src={photoFor(member, 40)} size={40} present={!!presentNow[member.id]} />}
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
            {member.name}
            {member.isActiveMk === false && <span title="ויתר/ה על המושב בכנסת לפי החוק הנורווגי"><Badge>נורווגי</Badge></span>}
          </span>
        }
        subtitle={
          <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.xxs }}>
            {primaryRole ? <span><RoleTag role={primaryRole} size="small" /></span> : member.profession ? <span>{member.profession}</span> : null}
            {sort && statVal !== null && (
              <span style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
                <Bar value={statVal} max={maxStatVal || 1} height={5} style={{ flex: 1 }} />
                <span style={{ fontSize: font.small, fontWeight: weight.bold, color: color.primary, flexShrink: 0 }}>{formatStatValue(statVal)}</span>
              </span>
            )}
          </span>
        }
      />
    );
  };

  return (
    <div style={{ padding: isDesktop ? 0 : `${space.md}px ${space.lg}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.md }}>
      {/* Floating button back to the tiles */}
      {showScrollUp && (
        <button
          type="button"
          aria-label="חזרה למדדים"
          onClick={() => statTilesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          style={{
            ...buttonReset,
            position: 'fixed', left: space.lg, bottom: isDesktop ? space.xxl : MOBILE_NAV_CLEARANCE - space.sm, zIndex: 50,
            width: 44, height: 44, borderRadius: '50%', background: color.primary, color: color.textOnPrimary,
            display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: shadow.selected,
          }}
        >
          <Icon name="chevronUp" size={22} strokeWidth={2.4} />
        </button>
      )}

      <div ref={statTilesRef} style={{ scrollMarginTop: 64 }}>
        <MetricsCard
          title="מדדי הסיעה"
          groups={METRIC_GROUPS}
          labels={METRIC_LABEL}
          values={partyValue}
          sort={sort}
          dir={dir}
          onSort={pickSort}
          filters={visibleFilters.map(f => ({ key: f.key, label: f.label, count: filterCount(f.key) }))}
          filter={filter}
          onFilter={pickFilter}
        />
      </div>

      <div>
        <ResultBar
          action={(sort || filter !== 'all') ? <Button variant="ghost" onClick={reset} style={{ padding: `0 ${space.sm}px` }}>איפוס</Button> : undefined}
          style={{ margin: `-${space.xs}px 0` }}
        >
          {statusText}
        </ResultBar>
        {sort && statNote[sort] && <div style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5 }}>{statNote[sort]}</div>}
        {presentHere > 0 && <PresenceLegend count={presentHere} to="/parties?tab=presence&pOrder=party" state={{ from: partyName }} />}
      </div>

      {sort === 'expenses' && expenseBreakdown()}

      {sorted.length === 0
        ? <Card padding={0}><EmptyState icon="users" title="אין תוצאות" /></Card>
        : isDesktop
          // Desktop: as many 300px+ columns as fit (long role tags truncate)
          ? (
            <Grid onlyOnDesktop minColWidth={300} gap="10px">
              {sorted.map((m, i) => <Card key={m.id} padding={0}>{mkRow(m, i, true)}</Card>)}
            </Grid>
          )
          : <Card padding={0}>{sorted.map((m, i) => mkRow(m, i, false))}</Card>}
    </div>
  );
};

/* ── Main Page ───────────────────────────────────────────────────────────── */

const MANIFESTO_TOPICS: { key: keyof Party['manifesto']; label: string }[] = [
  { key: 'security', label: 'ביטחון ומדיניות חוץ' },
  { key: 'economy', label: 'כלכלה' },
  { key: 'religion', label: 'דת ומדינה' },
  { key: 'ruleOfLaw', label: 'שלטון החוק' },
  { key: 'society', label: 'חברה' },
];

const PartyDetailPage = () => {
  const { partyId } = useParams();
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoint();
  const [searchParams, setSearchParams] = useUrlParams(); // keeps the caller's `from` across tab switches
  const [allParties, setAllParties] = useState<Party[] | null>(null);
  const [chairLobbyists, setChairLobbyists] = useState<Record<string, PartyChairLobbyists>>({});
  const [leaderId, setLeaderId] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Parties of every Knesset (the party may be an earlier Knesset's; the seat bar shows its own)
  useEffect(() => {
    let cancelled = false;
    setError(false);
    getParties().then(ps => { if (!cancelled) setAllParties(ps); }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const party = allParties ? allParties.find(p => p.id === partyId) || null : null;
  const partyKnesset = party ? party.knessetNumber ?? UNTAGGED_KNESSET : undefined;
  // Whether the party's Knesset has a coalition yet (none before a government is sworn in)
  const knessetHasCoalition = !allParties || hasCoalition(allParties.filter(p => (p.knessetNumber ?? UNTAGGED_KNESSET) === partyKnesset));

  // The chaired-committee lobbyist counts of the party's Knesset
  useEffect(() => {
    if (partyKnesset === undefined) return;
    let cancelled = false;
    getPartyChairLobbyistsOf(partyKnesset).then(c => { if (!cancelled) setChairLobbyists(c); }).catch(() => { /* ok — the tile is optional */ });
    return () => { cancelled = true; };
  }, [partyKnesset, reloadKey]);

  // The leader is stored by name; link it when exactly one MK of the party has that name
  useEffect(() => {
    if (!party?.leaderName) return;
    let cancelled = false;
    getAllMembersOf(partyKnesset).then(all => {
      if (cancelled) return;
      const matches = all.filter(m => m.partyId === party.id && m.name === party.leaderName);
      setLeaderId(matches.length === 1 ? matches[0].id : null);
    }).catch(() => { /* ok */ });
    return () => { cancelled = true; };
  }, [party?.id, party?.leaderName, partyKnesset]);

  // Scroll restoration: save on unmount, restore once the party has loaded
  const restored = useRef(false);
  useEffect(() => {
    if (!partyId) return;
    const key = `party_scroll_${partyId}`;
    return () => { try { sessionStorage.setItem(key, String(window.scrollY)); } catch { /* ok */ } };
  }, [partyId]);
  useEffect(() => {
    if (!partyId || restored.current || !party) return;
    restored.current = true;
    try {
      const saved = sessionStorage.getItem(`party_scroll_${partyId}`);
      if (saved) { window.scrollTo(0, parseInt(saved, 10) || 0); sessionStorage.removeItem(`party_scroll_${partyId}`); }
    } catch { /* ok */ }
  }, [partyId, party]);

  if (error) return <Layout><div style={{ padding: space.lg }}><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></div></Layout>;
  if (!allParties) {
    return (
      <Layout>
        <div data-skeleton="true" style={{ padding: space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <Card><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: space.md }}><Skeleton width={96} height={96} rounded={24} /><Skeleton width="50%" height={24} /><SkeletonText lines={1} /></div></Card>
          <SkeletonList rows={5} avatar />
        </div>
      </Layout>
    );
  }
  if (!party) {
    return (
      <Layout>
        <div style={{ padding: space.lg }}>
          <Card padding={0}>
            <EmptyState icon="users" title="הסיעה לא נמצאה" body="ייתכן שהקישור שגוי." action={<Button onClick={() => navigate('/parties', { replace: true })}>לכל הסיעות</Button>} />
          </Card>
        </div>
      </Layout>
    );
  }

  const manifesto = party.manifesto || {};
  const hasManifesto = MANIFESTO_TOPICS.some(t => manifesto[t.key]);
  // The tab lives in the URL; a manifesto link for a party without one shows the members
  const tabParam = searchParams.get('tab');
  const activeTab: PartyTab = tabParam === 'manifesto' && hasManifesto ? 'manifesto' : tabParam === 'funding' ? 'funding' : 'members';
  const setActiveTab = (tab: PartyTab) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (tab === 'members') next.delete('tab'); else next.set('tab', tab);
    return next;
  }, { replace: true });

  // Short name as the title; the full official name only as a subtitle when it differs
  const officialName = (party.name || '').trim();
  const displayName = shortPartyName(officialName);

  const logo = party.logoUrl
    ? (
      <span style={{ width: 96, height: 96, borderRadius: 24, background: color.surface, boxShadow: shadow.raised, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
        <img src={party.logoUrl} alt={`הלוגו של ${displayName}`} width={84} height={84} decoding="async"
          style={{ width: 84, height: 84, objectFit: 'contain', transform: party.logoScale ? `scale(${party.logoScale})` : undefined }}
          onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
      </span>
    )
    : <Avatar name={displayName} size={96} shape="rounded" />;

  const headerInfo = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: space.sm }}>
      <div>
        <h1 style={text.entityTitle}>{displayName}</h1>
        {officialName !== displayName && <div style={{ fontSize: font.small, color: color.textSecondary, marginTop: space.xxs }}>{officialName}</div>}
      </div>
      <span style={{ display: 'flex', gap: space.xs + 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        {knessetHasCoalition && <Badge tone={party.isCoalition ? 'info' : 'danger'} shape="pill">{party.isCoalition ? 'קואליציה' : 'אופוזיציה'}</Badge>}
        {party.politicalWing && <Badge shape="pill">{party.politicalWing}</Badge>}
      </span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: space.xs }}>
        <span style={{ fontSize: 28, fontWeight: weight.bold, color: color.primary, lineHeight: 1 }}>{party.seats}</span>
        <span style={{ fontSize: font.small, color: color.textSecondary }}>מנדטים</span>
      </div>
      {party.leaderName && (
        <div style={{ fontSize: font.small, color: color.textSecondary }}>
          יו״ר: {leaderId
            ? <Link to={`/members/${leaderId}`} state={{ from: displayName }} style={{ fontWeight: weight.medium, color: color.primary, textDecoration: 'none' }}>{party.leaderName}</Link>
            : <span style={{ fontWeight: weight.medium, color: color.text }}>{party.leaderName}</span>}
        </div>
      )}
    </div>
  );

  const seatBar = <SeatBar allParties={allParties.filter(p => (p.knessetNumber ?? UNTAGGED_KNESSET) === partyKnesset)} currentPartyId={party.id} />;

  const tabsBar = (
    <div style={{ padding: isDesktop ? `0 0 ${space.lg}px` : `${space.md}px ${space.lg}px 0` }}>
      <TabBar
        label="לשוניות הסיעה"
        tabs={[{ key: 'members', label: 'חברי כנסת' }, ...(hasManifesto ? [{ key: 'manifesto' as const, label: 'מצע' }] : []), { key: 'funding' as const, label: 'מימון' }]}
        active={activeTab}
        onChange={setActiveTab}
        style={isDesktop ? { maxWidth: 360 } : undefined}
      />
    </div>
  );

  // Phone/tablet: the colored band with the logo, then the seat bar. Desktop: both in the side panel.
  const side = isDesktop
    ? (
      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: space.md }}>
          {logo}
          {headerInfo}
        </div>
        <div style={{ borderTop: `1px solid ${color.divider}`, marginTop: space.lg, paddingTop: space.lg }}>{seatBar}</div>
      </Card>
    )
    : (
      <>
        <DetailHeader fallback="/parties" fallbackLabel="הכנסת" avatar={logo} bandColor={party.color || color.primaryInk}>
          {headerInfo}
        </DetailHeader>
        <div style={{ padding: `${space.lg}px ${space.lg}px 0` }}><Card>{seatBar}</Card></div>
      </>
    );

  return (
    <Layout width="full" breadcrumbs={[{ label: displayName }]}>
      <PageContainer>
        {/* Desktop: the tabs sit above the main column, so the side panel lines up with the content below them */}
        <TwoPane sideWidth={300} side={side} mainHeader={tabsBar}>
          {activeTab === 'manifesto' && (
            <div style={{ padding: isDesktop ? 0 : `${space.md}px ${space.lg}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.md }}>
              {/* Desktop: two columns keeps the paragraphs at a readable line length */}
              <Grid onlyOnDesktop cols={{ desktop: 2 }} gap="12px">
                {MANIFESTO_TOPICS.filter(t => manifesto[t.key]).map(({ key, label }) => (
                  <Card key={key} style={isDesktop ? undefined : { marginBottom: space.md }}>
                    <CardHeader title={label} />
                    <p style={{ ...text.body, margin: 0, lineHeight: 1.7 }}>{manifesto[key]}</p>
                  </Card>
                ))}
              </Grid>
              {party.manifestoUrl && (
                <a href={party.manifestoUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: space.xs + 2, minHeight: 44, borderRadius: radius.lg, background: color.primarySoft, color: color.primary, fontSize: font.label, fontWeight: weight.medium, textDecoration: 'none' }}>
                  <Icon name="external" size={16} />למידע נוסף על המפלגה
                </a>
              )}
            </div>
          )}

          {activeTab === 'members' && (
            <MembersList partyId={party.id} partyName={displayName} chairLobbyists={chairLobbyists[party.id] || null} knesset={partyKnesset} />
          )}

          {/* Mounted only when open, so its doc loads on first view */}
          {activeTab === 'funding' && <PartyFundingTab partyId={party.id} partyName={displayName} knesset={partyKnesset} />}
        </TwoPane>
      </PageContainer>
    </Layout>
  );
};

export default PartyDetailPage;
